import type { ReactNode } from "react"
import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import WestIcon from "@mui/icons-material/West"
import { Dialog, DialogContent, Grid, useMediaQuery, useTheme } from "@mui/material"
import { Button, InputText, RadioButton, Typography } from "@synapse-analytics/synapse-ui"

import { Model } from "../../types/generated/api/Model"

type DialogTextTypeProps = {
  dialogText: string | React.ReactElement
  subText?: string | React.ReactElement
  primaryBtnMode: "dangerous" | "secondary" | "primary"
  primaryBtnText?: string
  secondaryBtnMode: "dangerous" | "secondary" | "primary"
  secondaryBtnText?: string
}
/**
 * helper function to get dialog data based on the dialog mode
 * @param  {string} mode dialog mode
 * @param  {string} newState (state switch dialog's) new state
 * @param  {string} name resource name to appear on dialog
 * @return  {dialogTextType}
 */
const GetContent = (props: BaseSimpleDialogProps): DialogTextTypeProps => {
  const { mode, newState, name, list } = props

  const { t } = useTranslation("dialogs", {
    keyPrefix: "BaseSimpleDialog",
  })

  switch (mode) {
    case "project-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("projectDeletion.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("projectDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("projectDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("projectDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("projectDeletion.secondaryBtnText"),
      }
    case "model-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("modelDeletion.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("modelDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("modelDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("modelDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("modelDeletion.secondaryBtnText"),
      }
    case "training-data-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("trainingDataDeletion.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("trainingDataDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("trainingDataDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("trainingDataDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("trainingDataDeletion.secondaryBtnText"),
      }

    case "rule-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("ruleDeletion.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("ruleDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("ruleDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("ruleDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("ruleDeletion.secondaryBtnText"),
      }
    case "ruleset-deletion":
    case "scorecardset-deletion":
    case "program-deletion":
    case "script-deletion":
    case "taglist-deletion":
    case "scorecard-deletion":
    case "scoretable-deletion":
    case "calculator-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("workflowDeprecationWarning.dialogTextPart1")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>{" "}
            {t("workflowDeprecationWarning.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("workflowDeprecationWarning.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("workflowDeprecationWarning.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("workflowDeprecationWarning.secondaryBtnText"),
      }
    case "member-leaving":
      return {
        dialogText: (
          <Fragment>
            {t("memberLeaving.dialogText")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            ?
          </Fragment>
        ),
        subText: t("memberLeaving.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("memberLeaving.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("memberLeaving.secondaryBtnText"),
      }
    case "member-removal":
      return {
        dialogText: (
          <Fragment>
            {t("memberRemoval.dialogText")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            ?
          </Fragment>
        ),
        subText: t("memberRemoval.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("memberRemoval.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("memberRemoval.secondaryBtnText"),
      }
    case "organization-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("organizationDeletion.dialogText")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            ?
          </Fragment>
        ),
        subText: t("organizationDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("organizationDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("organizationDeletion.secondaryBtnText"),
      }
    case "invitation-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("invitationDeletion.dialogTextPart1")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("invitationDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("invitationDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("invitationDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("invitationDeletion.secondaryBtnText"),
      }
    case "role-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("roleDeletion.dialogText")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            ?
          </Fragment>
        ),
        subText: t("roleDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("roleDeletion.primaryBtnText", { roleName: name }),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("roleDeletion.secondaryBtnText"),
      }
    case "group-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("groupDeletion.dialogText")}{" "}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            ?
          </Fragment>
        ),
        subText: t("groupDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("groupDeletion.primaryBtnText", { groupName: name }),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("groupDeletion.secondaryBtnText"),
      }
    case "workflow-switch":
      return {
        dialogText: (
          <Fragment>
            {t("workflowSwitch.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("workflowSwitch.to")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {newState === "DISABLED" ? "IDLE" : "ACTIVE"}
            </Typography>
            {t("workflowSwitch.dialogTextPart2")}
          </Fragment>
        ),
        primaryBtnMode: "primary",
        primaryBtnText: t("workflowSwitch.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("workflowSwitch.secondaryBtnText"),
      }
    case "model-switch":
      return {
        dialogText: name,
        primaryBtnMode: "primary",
        primaryBtnText: t("modelSwitch.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("modelSwitch.secondaryBtnText"),
      }
    case "pending-model-switch":
      return {
        dialogText: t("pendingModelSwitch.dialogText"),
        primaryBtnMode: "primary",
        primaryBtnText: t("pendingModelSwitch.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("pendingModelSwitch.secondaryBtnText"),
      }

    case "close-while-edit":
      return {
        dialogText: t("closeWhileEdit.dialogText"),
        subText: t("closeWhileEdit.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("closeWhileEdit.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("closeWhileEdit.secondaryBtnText"),
      }
    case "remove-filter-node":
      return {
        dialogText: t("removeFilterNode.dialogText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("removeFilterNode.primaryBtnText"),
        subText: t("removeFilterNode.subText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("removeFilterNode.secondaryBtnText"),
      }
    case "remove-loop-node":
      return {
        dialogText: t("removeLoopNode.dialogText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("removeLoopNode.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("removeLoopNode.secondaryBtnText"),
      }
    case "deprecate-workflows":
      return {
        dialogText: t("deprecateWorkflows.dialogText"),
        subText: (
          <Fragment>
            {list && list?.length > 0 && (
              <ul>
                {list?.map((item) => (
                  <li key={`${item}`}>
                    <Typography variant="p">{item}</Typography>
                  </li>
                ))}
              </ul>
            )}
            <Typography variant="p" gutterBottom>
              {t("deprecateWorkflows.questionText")}
            </Typography>
          </Fragment>
        ),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("deprecateWorkflows.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("deprecateWorkflows.secondaryBtnText"),
      }
    case "view-mode-deprecate-workflows":
      return {
        dialogText: t("viewModeDeprecateWorkflows.dialogText"),
        subText: (
          <Fragment>
            {list && list?.length > 0 && (
              <ul>
                {list?.map((item) => (
                  <li key={`${item}`}>
                    <Typography variant="p">{item}</Typography>
                  </li>
                ))}
              </ul>
            )}
            <Typography variant="p" gutterBottom>
              {t("viewModeDeprecateWorkflows.questionText")}
            </Typography>
          </Fragment>
        ),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("viewModeDeprecateWorkflows.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("viewModeDeprecateWorkflows.secondaryBtnText"),
      }
    case "export-table":
      return {
        dialogText: t("exportTable.dialogText"),
        primaryBtnMode: "primary",
        primaryBtnText: t("exportTable.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("exportTable.secondaryBtnText"),
      }
    case "data-file-deletion":
      return {
        dialogText: (
          <Fragment>
            {t("dataFileDeletion.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("dataFileDeletion.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("dataFileDeletion.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("dataFileDeletion.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("dataFileDeletion.secondaryBtnText"),
      }
    case "data-file-cancelation":
      return {
        dialogText: (
          <Fragment>
            {t("dataFileCancelation.dialogTextPart1")}
            <Typography variant={"h2-bold"} variantColor={2} style={{ display: "inline-flex" }}>
              {name}
            </Typography>
            {t("dataFileCancelation.dialogTextPart2")}
          </Fragment>
        ),
        subText: t("dataFileCancelation.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("dataFileCancelation.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("dataFileCancelation.secondaryBtnText"),
      }

    case "delete-config":
      return {
        dialogText: t("deleteConfig.dialogText"),
        subText: t("deleteConfig.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("deleteConfig.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("deleteConfig.secondaryBtnText"),
      }

    case "actual-decision":
      return {
        dialogText: t("actualDecision.dialogText"),
        primaryBtnMode: "primary",
        primaryBtnText: t("actualDecision.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("actualDecision.secondaryBtnText"),
      }

    case "challenger-model-replace":
      return {
        dialogText: t("challengerModelReplace.dialogText"),
        primaryBtnMode: "primary",
        primaryBtnText: t("challengerModelReplace.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("challengerModelReplace.secondaryBtnText"),
      }

    default:
      // if this message appears that means something went wrong in the switch case
      return {
        dialogText: t("defaultDialog.dialogText"),
        subText: t("defaultDialog.subText"),
        primaryBtnMode: "dangerous",
        primaryBtnText: t("defaultDialog.primaryBtnText"),
        secondaryBtnMode: "secondary",
        secondaryBtnText: t("defaultDialog.secondaryBtnText"),
      }
  }
}

export type BaseSimpleDialogProps = {
  children?: ReactNode
  disableMainBtn?: boolean
  open: boolean
  isLoading: boolean
  name: string
  newState?: string
  list?: Array<string>
  onAccept: () => void
  onClose: () => void
  onBack?: () => void
  mode:
    | "project-deletion"
    | "model-deletion"
    | "rule-deletion"
    | "ruleset-deletion"
    | "scorecardset-deletion"
    | "scorecard-deletion"
    | "scoretable-deletion"
    | "program-deletion"
    | "taglist-deletion"
    | "calculator-deletion"
    | "script-deletion"
    | "model-switch"
    | "workflow-switch"
    | "close-while-edit"
    | "organization-deletion"
    | "member-leaving"
    | "member-removal"
    | "invitation-deletion"
    | "role-deletion"
    | "group-deletion"
    | "training-data-deletion"
    | "deprecate-workflows"
    | "view-mode-deprecate-workflows"
    | "pending-model-switch"
    | "fix-model-schema"
    | "remove-filter-node"
    | "remove-loop-node"
    | "export-table"
    | "data-file-deletion"
    | "data-file-cancelation"
    | "delete-config"
    | "actual-decision"
    | "pending-actual-decisions-file"
    | "challenger-model-replace"
}

/**
 * simple dialog base to use in simple repeatable cases
 * @param  {boolean} open is dialog open?
 * @param  {boolean} isLoading if true triggers is loading mode
 * @param  {string} name project/model name
 * @param  {string} newState model switch state to be
 * @param  {function} onAccept function to fire up on click of the main btn of the dialog
 * @param  {function} onClose function to fire up on dialog close
 * @param  {function} onBack function to fire up when the back button is clicked
 * @return  {React.ReactElement}
 */
export function BaseSimpleDialog(props: Readonly<BaseSimpleDialogProps>): React.ReactElement {
  const { children, open, isLoading, onClose, onAccept, onBack, disableMainBtn = false } = props

  const theme = useTheme()

  const content = GetContent(props)

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && isLoading) {
          return undefined
        } else {
          onClose()
        }
      }}
      fullWidth
      fullScreen={useMediaQuery(theme.breakpoints.down("sm"))}
      maxWidth="xs"
      PaperProps={{
        style: {
          // To enable the date-picker to overflow
          overflow: "unset",
        },
      }}
    >
      <DialogContent className={`dialog-base`} style={{ borderRadius: "8px" }}>
        <Grid container gap={2}>
          <Grid item container xs={12} gap={1.5} alignItems="center">
            {onBack && (
              <Button size="small" onClick={onBack} variant="ghost">
                <WestIcon style={{ color: "var(--secondary-icon-default)", width: "16px", height: "16px" }} />
              </Button>
            )}
            <Typography variant="h2-bold" display="inline">
              {content.dialogText}
            </Typography>
          </Grid>

          {content.subText && (
            <Grid item xs={12}>
              <Typography variant="p" gutterBottom>
                {content.subText}
              </Typography>
            </Grid>
          )}

          {children && (
            <Grid item xs={12}>
              {children}
            </Grid>
          )}

          <Grid container item xs={12} gap={1.5}>
            {content.primaryBtnText && (
              <Grid item xs={12}>
                <Button
                  variant={content.primaryBtnMode}
                  onClick={onAccept}
                  id="main_btn"
                  fullWidth
                  disabled={disableMainBtn}
                  size="large"
                  isLoading={isLoading}
                >
                  {content.primaryBtnText}
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                variant={content.secondaryBtnMode}
                onClick={onClose}
                disabled={isLoading}
                autoFocus
                fullWidth
                size="large"
              >
                {content.secondaryBtnText}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export function ConfirmationSimpleDialog(props: Readonly<BaseSimpleDialogProps>): React.ReactElement {
  const { name } = props

  const [value, setValue] = useState<string>("")
  const { t } = useTranslation("dialogs", {
    keyPrefix: "BaseSimpleDialog",
  })

  return (
    <BaseSimpleDialog {...props} disableMainBtn={name !== value}>
      <InputText
        label={t("confirmation.label", { name })}
        id="confirmation"
        value={value}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        handleChange={(e: React.ChangeEvent<Element>) => setValue(e.target.value)}
        placeholder={`${name}`}
        fullWidth
      />
    </BaseSimpleDialog>
  )
}

type SwitchModelDialogProps = Omit<BaseSimpleDialogProps, "onAccept"> & {
  modelState: "live" | "disabled" | "challenger" | "pending-action"
  onAccept: (newState: string, modelToReplace?: Model) => Promise<void>
  challengerModels?: Model[]
}

/**
 * Base Simple Dialog, customized for model switch flow (used in model card and model header)
 * @param {string} modelState current model state
 * @param {function} onAccept handler for switching action
 * @param {Model[]} challengerModels list of challenger models
 * @returns {React.ReactElement}
 */
export function ModelSwitchDialog(props: SwitchModelDialogProps): React.ReactElement {
  const { modelState, onAccept, mode, challengerModels, disableMainBtn } = props

  const { t } = useTranslation("dialogs", {
    keyPrefix: "BaseSimpleDialog",
  })

  const modelStates = [
    {
      name: t("modelSwitchDialog.states.idle.name"),
      description: t("modelSwitchDialog.states.idle.description"),
      state: "disabled",
    },
    {
      name: t("modelSwitchDialog.states.challenger.name"),
      description: t("modelSwitchDialog.states.challenger.description"),
      state: "challenger",
    },
    {
      name: t("modelSwitchDialog.states.live.name"),
      description: t("modelSwitchDialog.states.live.description"),
      state: "live",
    },
  ]

  const numberOfChallengers = challengerModels?.length

  const adjustedModelStates = modelStates.filter((model) => model.state !== modelState)

  const [newModelState, setNewModelState] = useState<string>(adjustedModelStates[0]?.state as string)
  const [modelToReplace, setModelToReplace] = useState<string | undefined>(undefined)
  const [step, setStep] = useState<number>(0)

  const steps = [
    <Fragment key="state">
      <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
        {adjustedModelStates.map((model) => {
          if (model.state === "challenger") {
            return (
              <Grid container justifyContent={"space-between"} alignItems={"center"} key={model.state}>
                <RadioButton
                  key={`${model.state}-radio`}
                  value={newModelState}
                  label={model.name}
                  id={model.name}
                  name={model.state}
                  checked={
                    newModelState === model.state &&
                    numberOfChallengers !== undefined &&
                    numberOfChallengers !== null &&
                    numberOfChallengers < 5
                  }
                  onChange={(e) => setNewModelState(e.target.name)}
                  description={model.description}
                  disabled={!!numberOfChallengers && numberOfChallengers >= 5}
                />

                <Grid item container gap={1} direction={"column"} alignItems={"end"} xs={3}>
                  <Typography
                    variant="label-regular"
                    color={numberOfChallengers && numberOfChallengers >= 5 ? "negative" : "positive"}
                    variantColor={2}
                  >
                    {t("modelSwitchDialog.challengerCount", { count: numberOfChallengers })}
                  </Typography>

                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setStep(1)}
                    disabled={numberOfChallengers === 0}
                  >
                    {t("modelSwitchDialog.replace")}
                  </Button>
                </Grid>
              </Grid>
            )
          }

          return (
            <RadioButton
              key={`${model.state}-radio`}
              value={newModelState}
              label={model.name}
              id={model.name}
              name={model.state}
              checked={newModelState === model.state}
              onChange={(e) => setNewModelState(e.target.name)}
              description={model.description}
            />
          )
        })}
      </Grid>

      {/* hide this sub text if any other mode is rendered */}
      {mode === "model-switch" && (
        <Grid item xs={12} mt={2}>
          <Typography variant="p">{t("modelSwitchDialog.switchBackInfo")}</Typography>
        </Grid>
      )}
    </Fragment>,
    <Fragment key="replace">
      <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
        {challengerModels?.map((model) => {
          return (
            <RadioButton
              key={model.uuid}
              value={model.uuid}
              label={model.name}
              id={model.uuid}
              name={model.name}
              checked={modelToReplace === model.name}
              onChange={(e) => setModelToReplace(e.target.name)}
            />
          )
        })}
      </Grid>
    </Fragment>,
  ]

  return (
    <BaseSimpleDialog
      {...props}
      onAccept={() =>
        onAccept(
          newModelState,
          challengerModels?.find((model) => model.name === modelToReplace),
        )
      }
      onBack={step === 1 ? () => setStep(0) : undefined}
      mode={step === 0 ? props.mode : "challenger-model-replace"}
      disableMainBtn={
        (numberOfChallengers === 5 && !modelToReplace && newModelState === "challenger") || disableMainBtn
      }
    >
      {steps[step]}
    </BaseSimpleDialog>
  )
}
